/** Minimal history router: pattern → params, `navigate()`, and a `<Link>` that keeps ?lang. */

import type { ComponentChildren, JSX } from "preact";
import { useEffect, useState } from "preact/hooks";

export interface Route {
  path: string;
  params: Record<string, string>;
  query: URLSearchParams;
}

const listeners = new Set<() => void>();

export function navigate(to: string, replace = false): void {
  if (replace) history.replaceState(null, "", to);
  else history.pushState(null, "", to);
  for (const l of listeners) l();
}

export function useLocation(): Route {
  const [, force] = useState(0);
  useEffect(() => {
    const l = () => force((n) => n + 1);
    listeners.add(l);
    window.addEventListener("popstate", l);
    return () => {
      listeners.delete(l);
      window.removeEventListener("popstate", l);
    };
  }, []);
  return { path: location.pathname, params: {}, query: new URLSearchParams(location.search) };
}

/** Match "/j/:code" style patterns. */
export function matchPath(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split("/").filter(Boolean);
  const a = path.split("/").filter(Boolean);
  if (p.length !== a.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    const seg = p[i]!;
    const val = a[i]!;
    if (seg.startsWith(":")) params[seg.slice(1)] = decodeURIComponent(val);
    else if (seg !== val) return null;
  }
  return params;
}

export function Link(
  props: JSX.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string; children?: ComponentChildren },
) {
  const { href, onClick, ...rest } = props;
  return (
    <a
      {...rest}
      href={href}
      onClick={(e) => {
        if (typeof onClick === "function") onClick(e);
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        if (href.startsWith("http") || rest.target === "_blank") return;
        e.preventDefault();
        navigate(href);
      }}
    >
      {props.children}
    </a>
  );
}
